import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';

const ProviderConversionBlock = () => {
  const benefits = [
    {
      icon: "Target",
      title: "Gekwalificeerde leads",
      description: "Bereik consumenten die actief op zoek zijn naar uw diensten"
    },
    {
      icon: "Star",
      title: "Reviews & zichtbaarheid",
      description: "Bouw vertrouwen op met echte klantbeoordelingen"
    },
    {
      icon: "TrendingUp",
      title: "Inhoudelijke positionering",
      description: "Presenteer uw expertise en onderscheid u van concurrenten"
    }
  ];

  const stats = [
    { value: "360", label: "Actieve aanbieders" },
    { value: "500+", label: "Leads per maand" },
    { value: "4.8/5", label: "Aanbieder tevredenheid" }
  ];

  return (
    <motion.section 
      className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-secondary/10 to-accent/10 relative overflow-hidden"
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      {/* Animated background waves */}
      <motion.div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "linear-gradient(45deg, transparent 25%, rgba(59, 130, 246, 0.1) 25%, rgba(59, 130, 246, 0.1) 50%, transparent 50%, transparent 75%, rgba(59, 130, 246, 0.1) 75%)",
          backgroundSize: "60px 60px"
        }}
        animate={{
          backgroundPosition: ["0px 0px", "60px 60px"],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="bg-card border border-border rounded-2xl shadow-[0_15px_40px_-8px_rgba(0,0,0,0.2)] hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden transform perspective-1000"
          whileHover={{ scale: 1.008, rotateY: 1, boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.3), 0 0 30px rgba(59, 130, 246, 0.1)" }}
          transition={{ duration: 0.3 }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Content */}
            <motion.div 
              className="p-8 lg:p-12"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ transform: 'translateZ(20px)' }}
            >
              <div className="mb-6">
                <motion.div 
                  className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-4 shadow-[0_8px_20px_-4px_rgba(0,0,0,0.15)]"
                  animate={{
                    rotate: [0, 10, -10, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  whileHover={{ 
                    scale: 1.2,
                    rotate: 360,
                    boxShadow: "0 12px 30px -5px rgba(0, 0, 0, 0.25)"
                  }}
                  style={{ transform: 'translateZ(30px)', transformStyle: 'preserve-3d' }}
                >
                  <Icon name="Building" size={32} className="text-secondary" />
                </motion.div>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
                  Bereik consumenten die écht willen verduurzamen
                </h2>
                <p className="text-lg font-body text-muted-foreground">
                  Word aanbieder op Nederland's grootste verduurzamingsplatform
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-6 mb-8">
                {benefits?.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start space-x-4 bg-card/50 backdrop-blur-sm rounded-lg p-4 shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.15)] transition-all"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    whileHover={{ x: 8, scale: 1.03, boxShadow: "0 8px 20px rgba(0,0,0,0.15)" }}
                    style={{ transform: 'translateZ(10px)' }}
                  >
                    <div className="flex-shrink-0">
                      <motion.div 
                        className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center shadow-[0_3px_10px_rgba(0,0,0,0.1)]"
                        whileHover={{ rotate: 360, scale: 1.15, boxShadow: "0 6px 16px rgba(0,0,0,0.2)" }}
                        transition={{ duration: 0.5 }}
                        style={{ transform: 'translateZ(15px)' }}
                      >
                        <Icon name={benefit?.icon} size={20} className="text-secondary" />
                      </motion.div>
                    </div>
                    <div>
                      <h3 className="text-base font-heading font-semibold text-foreground mb-1">
                        {benefit?.title}
                      </h3>
                      <p className="text-sm font-body text-muted-foreground">
                        {benefit?.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <Link to="/leverancier-registration">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button size="xl" variant="secondary" iconName="ArrowRight" iconPosition="right" fullWidth>
                    Word aanbieder op het platform
                  </Button>
                </motion.div>
              </Link>
              <p className="text-xs font-caption text-muted-foreground mt-3 text-center">
                Geen opstartkosten • Flexibele pakketten • Direct starten
              </p>
            </motion.div>

            {/* Stats Sidebar */}
            <motion.div 
              className="bg-gradient-to-br from-secondary to-secondary/80 p-8 lg:p-12 text-secondary-foreground relative overflow-hidden"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Animated background pattern */}
              <motion.div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
                  backgroundSize: "30px 30px"
                }}
                animate={{
                  backgroundPosition: ["0px 0px", "30px 30px"],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <h3 className="text-2xl font-heading font-bold mb-8 relative z-10">
                Waarom aanbieders voor ons kiezen
              </h3>
              
              <div className="space-y-8 mb-12 relative z-10">
                {stats?.map((stat, index) => (
                  <motion.div 
                    key={index}
                    className="bg-card/30 backdrop-blur-sm rounded-lg p-4 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)] transition-all"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: index * 0.2 
                    }}
                    whileHover={{ scale: 1.08, x: 10, boxShadow: "0 8px 20px rgba(0,0,0,0.2)" }}
                    style={{ transform: 'translateZ(15px)' }}
                  >
                    <div className="text-4xl font-heading font-bold mb-2">
                      {stat?.value}
                    </div>
                    <div className="text-sm font-body text-secondary-foreground/80">
                      {stat?.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div 
                className="border-t border-secondary-foreground/20 pt-8 relative z-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
              >
                <p className="text-sm font-body text-secondary-foreground/90 italic">
                  "Sinds we op Verduurzaamwijzer staan, hebben we 40% meer gekwalificeerde aanvragen ontvangen.
                  De leads zijn van hoge kwaliteit en de consumenten zijn goed geïnformeerd."
                </p>
                <p className="text-xs font-caption text-secondary-foreground/70 mt-3">
                  — Jan Bakker, SolarTech Nederland
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ProviderConversionBlock;